import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Smile, Meh, Frown, Users, AlertCircle } from "lucide-react";
import dayjs from "dayjs";

const satBadge = (s) => {
  if (s === "positive") return { cls: "bg-emerald-100 text-emerald-800", label: "Puas" };
  if (s === "neutral") return { cls: "bg-amber-100 text-amber-800", label: "Biasa" };
  return { cls: "bg-red-100 text-red-800", label: "Kurang Puas" };
};

const statusBadge = (s) => {
  if (!s) return null;
  const map = { "Baru": "bg-red-100 text-red-800", "Diproses": "bg-amber-100 text-amber-800", "Selesai": "bg-emerald-100 text-emerald-800" };
  return <Badge className={`${map[s]} font-medium border-0`}>{s}</Badge>;
};

const pctOf = (n, total) => (total > 0 ? Math.round((n / total) * 1000) / 10 : 0);

function OutletSummary({ o }) {
  const cards = [
    { key: "puas", label: "Puas", value: o.puas, p: pctOf(o.puas, o.total), bar: "bg-emerald-500", text: "text-emerald-700", icon: Smile },
    { key: "biasa", label: "Biasa", value: o.biasa, p: pctOf(o.biasa, o.total), bar: "bg-amber-500", text: "text-amber-700", icon: Meh },
    { key: "kurang", label: "Kurang Puas", value: o.kurang, p: pctOf(o.kurang, o.total), bar: "bg-red-500", text: "text-red-700", icon: Frown },
    { key: "total", label: "Total Feedback", value: o.total, p: o.total > 0 ? 100 : 0, bar: "bg-zinc-900", text: "text-zinc-900", icon: MessageSquare },
  ];
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm" data-testid={`outlet-summary-${o.outlet_id}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold tracking-tight">{o.name}</h3>
        <span className="text-xs text-zinc-400">/{o.slug}</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.key} className="rounded-xl border border-zinc-100 bg-zinc-50/60 p-4" data-testid={`outlet-summary-${o.outlet_id}-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${c.text}`}>{c.label}</span>
              <c.icon size={14} className={c.text} />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="text-2xl font-extrabold tracking-tight">{c.value}</span>
              <span className={`text-xs font-bold ${c.text}`} data-testid={`outlet-summary-${o.outlet_id}-${c.key}-pct`}>{c.p}%</span>
            </div>
            <div className="mt-2 h-1 rounded-full bg-zinc-200/70 overflow-hidden">
              <div className={`h-full rounded-full ${c.bar} transition-[width] duration-500`} style={{ width: `${c.p}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <Card className="border-zinc-200" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
        <div className={`w-8 h-8 rounded-lg grid place-items-center ${color}`}><Icon size={16} /></div>
      </div>
      <div className="text-3xl font-extrabold mt-3 tracking-tight" data-testid={`${testid}-value`}>{value}</div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [stats, setStats] = useState({ total: 0, puas: 0, biasa: 0, kurang: 0, unresolved: 0, total_customers: 0 });
  const [recent, setRecent] = useState([]);
  const [perOutlet, setPerOutlet] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, r, po] = await Promise.all([api.get("/stats"), api.get("/feedback?limit=10"), api.get("/stats/per-outlet")]);
      setStats(s.data); setRecent(r.data); setPerOutlet(po.data);
    })();
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Ringkasan Kinerja</h1>
        <p className="text-sm text-zinc-500 mt-1">Pantau feedback dan komplain pelanggan.</p>
      </div>

      <section data-testid="feedback-summary" className="space-y-4">
        <h2 className="text-lg font-semibold tracking-tight">Ringkasan Feedback per Outlet</h2>
        {perOutlet.length === 0
          ? <div className="text-sm text-zinc-400">Belum ada data outlet.</div>
          : perOutlet.map(o => <OutletSummary key={o.outlet_id} o={o} />)}
      </section>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard icon={MessageSquare} label="Total Feedback" value={stats.total} color="bg-zinc-100 text-zinc-700" testid="stat-total" />
        <StatCard icon={Smile} label="Puas" value={stats.puas} color="bg-emerald-100 text-emerald-700" testid="stat-puas" />
        <StatCard icon={Meh} label="Biasa Saja" value={stats.biasa} color="bg-amber-100 text-amber-700" testid="stat-biasa" />
        <StatCard icon={Frown} label="Kurang Puas" value={stats.kurang} color="bg-red-100 text-red-700" testid="stat-kurang" />
        <StatCard icon={Users} label="Total Pelanggan" value={stats.total_customers} color="bg-blue-100 text-blue-700" testid="stat-customers" />
        <StatCard icon={AlertCircle} label="Belum Ditangani" value={stats.unresolved} color="bg-orange-100 text-orange-700" testid="stat-unresolved" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <h2 className="font-semibold tracking-tight">Feedback Terbaru</h2>
          <Link to="/admin/feedback" className="text-sm text-emerald-700 hover:underline" data-testid="view-all-feedback-link">Lihat semua</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Nama</th>
                <th className="text-left px-5 py-3 font-semibold">Kepuasan</th>
                <th className="text-left px-5 py-3 font-semibold">Kategori</th>
                <th className="text-left px-5 py-3 font-semibold">Tanggal</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-400">Belum ada feedback.</td></tr>
              )}
              {recent.map(f => {
                const b = satBadge(f.satisfaction);
                return (
                  <tr key={f.id} className="border-t border-zinc-100" data-testid={`recent-feedback-row-${f.id}`}>
                    <td className="px-5 py-3 font-medium">{f.name || <span className="text-zinc-400">Anonim</span>}</td>
                    <td className="px-5 py-3"><Badge className={`${b.cls} border-0`}>{b.label}</Badge></td>
                    <td className="px-5 py-3 text-zinc-600">{f.category || "—"}</td>
                    <td className="px-5 py-3 text-zinc-500">{dayjs(f.created_at).format("DD MMM YYYY HH:mm")}</td>
                    <td className="px-5 py-3">{statusBadge(f.status)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
